import type { DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';
import type { ProfileLinkingApiError } from './types';
import type { CandidateDetailWire } from './directUps';

export interface ProfileLinkingModeResponse {
  dataSource: 'live' | 'fixture';
  liveConfigured: boolean;
}

function apiError(message: string, code?: string, status?: number): ProfileLinkingApiError {
  const err = new Error(message) as ProfileLinkingApiError;
  err.code = code;
  err.status = status;
  return err;
}

async function parseErrorResponse(response: Response): Promise<ProfileLinkingApiError> {
  const body = await response.json().catch(() => undefined);
  const code = body?.error?.code as string | undefined;
  const message =
    body?.error?.message ?? `Profile Linking request failed (${response.status})`;
  return apiError(message, code, response.status);
}

export async function fetchProfileLinkingMode(
  discoveryApi: DiscoveryApi,
  fetchApi: FetchApi,
): Promise<ProfileLinkingModeResponse> {
  const baseUrl = await discoveryApi.getBaseUrl('profile-linking');
  const response = await fetchApi.fetch(`${baseUrl}/mode`);
  if (!response.ok) throw await parseErrorResponse(response);
  return (await response.json()) as ProfileLinkingModeResponse;
}

export interface SearchParams {
  network: string;
  handle?: string;
  profileId?: string;
}

// Tokenless: fetches raw candidate+evaluation data only. Used by the
// embedded Unified Profiles page, which resolves/hydrates UPS profiles
// directly from the browser and only needs Storage data from Backstage.
export async function fetchLinkingCandidateDetails(
  discoveryApi: DiscoveryApi,
  fetchApi: FetchApi,
  sourceNetwork: string,
  sourceProfileId: string,
  limit?: number,
): Promise<CandidateDetailWire[]> {
  const baseUrl = await discoveryApi.getBaseUrl('profile-linking');
  const query = new URLSearchParams({ sourceNetwork, sourceProfileId });
  if (limit) query.set('limit', String(limit));

  const response = await fetchApi.fetch(`${baseUrl}/candidates?${query}`);
  if (!response.ok) throw await parseErrorResponse(response);
  const body = (await response.json()) as { details: CandidateDetailWire[] };
  return body.details;
}

// Reproducible evidence retrieval: the EXACT immutable image an evaluation's
// image comparison analyzed, proxied through the Backstage backend (never a
// direct browser call to Linking Storage/blob storage). Distinguishes a true
// 404 ("not-found" -- no stored image for this evaluation/side, an expected,
// unremarkable outcome for e.g. a legacy-path evaluation) from a network
// error or unexpected non-2xx ("error" -- a real failure worth a different
// message) -- this is a soft enhancement either way, so a caller never
// surfaces an error UI, just picks a different fallback label.
export type EvidenceImageFetchResult =
  | { kind: 'ok'; blob: Blob }
  | { kind: 'not-found' }
  | { kind: 'error' };

export async function fetchEvidenceImage(
  discoveryApi: DiscoveryApi,
  fetchApi: FetchApi,
  evaluationId: string,
  side: 'source' | 'target',
): Promise<EvidenceImageFetchResult> {
  try {
    const baseUrl = await discoveryApi.getBaseUrl('profile-linking');
    const response = await fetchApi.fetch(
      `${baseUrl}/evidence-images/${encodeURIComponent(evaluationId)}/${side}`,
    );
    if (response.status === 404) return { kind: 'not-found' };
    if (!response.ok) return { kind: 'error' };
    return { kind: 'ok', blob: await response.blob() };
  } catch {
    return { kind: 'error' };
  }
}
